// Web Storage throws DOMException for SecurityError (private browsing,
// third-party cookies blocked) and QuotaExceededError (full). Anything else
// (e.g. a TypeError) is a real bug — let it propagate.
//
// WHY: Node 25+ exposes a partial `localStorage` global without `getItem` on
// import — the methods are missing until the runtime is fully initialised.
// Treat that case the same as a SecurityError-style "no storage available"
// (return null / no-op) instead of crashing module init.
function safeGet(storage: Storage, key: string): string | null {
    if (typeof storage.getItem !== 'function') return null
    try {
        return storage.getItem(key)
    } catch (e) {
        if (!(e instanceof DOMException)) throw e
        return null
    }
}

function safeSet(storage: Storage, key: string, value: string): void {
    if (typeof storage.setItem !== 'function') return
    try {
        storage.setItem(key, value)
    } catch (e) {
        if (!(e instanceof DOMException)) throw e
    }
}

function safeRemove(storage: Storage, key: string): void {
    if (typeof storage.removeItem !== 'function') return
    try {
        storage.removeItem(key)
    } catch (e) {
        if (!(e instanceof DOMException)) throw e
    }
}

// JSON.parse throws SyntaxError on malformed input — drop the corrupt blob
// and start clean. Other errors (e.g. circular refs from JSON.stringify on
// the way in) shouldn't reach JSON.parse, but if they do they're real bugs.
function safeParseJSON<T>(raw: string): T | null {
    try {
        return JSON.parse(raw) as T
    } catch (e) {
        if (!(e instanceof SyntaxError)) throw e
        return null
    }
}

// WHY: spec files that import production modules (e.g. translation strings)
// trigger module-level i18n init which calls safeLocalStorage. In Node /
// Playwright collection there is no `localStorage` global — guard the lookup
// so the import doesn't throw ReferenceError before the test even runs.
function ls(): Storage | null {
    return typeof globalThis !== 'undefined' && typeof (globalThis as {localStorage?: Storage}).localStorage !== 'undefined'
        ? (globalThis as {localStorage: Storage}).localStorage
        : null
}

export const safeLocalStorage = {
    get: (key: string) => {
        const s = ls(); return s ? safeGet(s, key) : null
    },
    set: (key: string, value: string) => {
        const s = ls(); if (s) safeSet(s, key, value)
    },
    remove: (key: string) => {
        const s = ls(); if (s) safeRemove(s, key)
    },
    getJSON: <T>(key: string): T | null => {
        const s = ls(); if (!s) return null
        const raw = safeGet(s, key)
        return raw ? safeParseJSON<T>(raw) : null
    },
    setJSON: (key: string, value: unknown) => {
        const s = ls(); if (s) safeSet(s, key, JSON.stringify(value))
    },
}

function ss(): Storage | null {
    return typeof globalThis !== 'undefined' && typeof (globalThis as {sessionStorage?: Storage}).sessionStorage !== 'undefined'
        ? (globalThis as {sessionStorage: Storage}).sessionStorage
        : null
}

export const safeSessionStorage = {
    get: (key: string) => {
        const s = ss(); return s ? safeGet(s, key) : null
    },
    set: (key: string, value: string) => {
        const s = ss(); if (s) safeSet(s, key, value)
    },
    remove: (key: string) => {
        const s = ss(); if (s) safeRemove(s, key)
    },
    getJSON: <T>(key: string): T | null => {
        const s = ss(); if (!s) return null
        const raw = safeGet(s, key)
        return raw ? safeParseJSON<T>(raw) : null
    },
    setJSON: (key: string, value: unknown) => {
        const s = ss(); if (s) safeSet(s, key, JSON.stringify(value))
    },
}
